import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Upload,
  Camera,
  FileText,
  CreditCard,
  Sparkles,
  Printer,
  RotateCw,
  RotateCcw,
  Sliders,
  CheckCircle2,
  AlertCircle,
  Crop,
  Eye,
  RefreshCw,
  Trash2,
  Copy,
  ArrowRight,
  ArrowLeft,
  Layers,
  Maximize2,
  ZoomIn,
  Settings,
  ChevronRight,
  ShieldCheck,
  FolderOpen,
  Plus,
  SplitSquareVertical,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  createCanvas,
  loadImage,
  canvasToDataURL,
  canvasToBlob,
  rotateCanvas,
  perspectiveWarp,
  detectDocumentCorners,
  enhanceImage,
  renderDocumentA4Layout,
  renderIdCardA4Layout,
  ID_CARD_PHYSICAL,
} from "@/lib/imageEnhancer";
import { renderPdfToCanvases } from "@/lib/pdfLoader";
import DocumentCropEditor from "@/components/DocumentCropEditor";

const PRESETS = [
  { id: "original", label: "Original", icon: Eye },
  { id: "auto", label: "Auto Enhance", icon: Sparkles },
  { id: "document", label: "Document (Magic)", icon: FileText },
  { id: "bw", label: "B&W Xerox", icon: Layers },
  { id: "grayscale", label: "Grayscale", icon: Sliders },
  { id: "clean_bg", label: "Clean Background", icon: Sparkles },
  { id: "light", label: "Light", icon: ZoomIn },
  { id: "dark", label: "Dark", icon: Maximize2 },
];

export default function ScanStudio() {
  // Stepper: 1: Upload, 2: Enhance, 3: Layout, 4: Preview, 5: Print
  const [currentStep, setCurrentStep] = useState(1);

  // Mode: 'document' (A4 Document) | 'id_card' (Front + Back ID Card)
  const [mode, setMode] = useState("document");

  // Multi-page document state
  const [pages, setPages] = useState([]);
  const [activePageIndex, setActivePageIndex] = useState(0);

  // ID Card slots
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [activeIdSlot, setActiveIdSlot] = useState("front"); // 'front' | 'back'
  const [idLayout, setIdLayout] = useState("stacked"); // 'stacked' | 'side_by_side'
  const [showCutGuides, setShowCutGuides] = useState(true);

  // Document A4 Layout options
  const [a4FitMode, setA4FitMode] = useState("fit");
  const [a4MarginMm, setA4MarginMm] = useState(8);
  const [a4Orientation, setA4Orientation] = useState("portrait");

  // Print settings
  const [copies, setCopies] = useState(1);
  const [printType, setPrintType] = useState("bw"); // 'bw' | 'color'
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [shopSetup, setShopSetup] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printProgressStep, setPrintProgressStep] = useState("");

  // Crop / Perspective warp editor state
  const [isCropMode, setIsCropMode] = useState(false);
  const [cropCorners, setCropCorners] = useState(null);

  // Before / After interactive split slider (0% to 100%)
  const [comparisonSplit, setComparisonSplit] = useState(50);
  const [isComparing, setIsComparing] = useState(false);

  // Fine adjustments panel toggle
  const [showAdjustDrawer, setShowAdjustDrawer] = useState(false);

  // Camera capture modal
  const [showCameraModal, setShowCameraModal] = useState(false);
  const videoRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);

  // Drag & drop highlight state
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  // -------------------------------------------------------------
  // Load Printers & Shop Configuration
  // -------------------------------------------------------------
  const loadPrintersAndShop = useCallback(async () => {
    try {
      // 1. Detect installed Windows printers via Electron IPC
      if (window.jsmDesktop?.printers?.getAll) {
        try {
          const list = await window.jsmDesktop.printers.getAll();
          if (Array.isArray(list)) {
            setPrinters(list.filter((p) => p.name));
          }
        } catch (e) {
          console.warn("Printer detection notice:", e);
        }
      }

      // 2. Load shop printer mapping from backend
      const res = await api.get("/scan-to-print/shop");
      if (res.data?.shop) {
        setShopSetup(res.data.shop);
        const machines = res.data.shop.machines || [];
        const defaultMachine = machines.find(
          (m) => m.machine_type === printType && m.enabled !== false
        ) || machines[0];

        if (defaultMachine) {
          setSelectedPrinter(defaultMachine.device_name || defaultMachine.machine_name);
        }
      }
    } catch (err) {
      console.warn("Shop setup load notice:", err);
    }
  }, [printType]);

  useEffect(() => {
    loadPrintersAndShop();
  }, [loadPrintersAndShop]);

  useEffect(() => {
    if (shopSetup?.machines) {
      const target = shopSetup.machines.find(
        (m) => m.machine_type === printType && m.enabled !== false
      );
      if (target) {
        setSelectedPrinter(target.device_name || target.machine_name);
      }
    }
  }, [printType, shopSetup]);

  // -------------------------------------------------------------
  // Re-render Enhancement for Active Page or ID Card Slot
  // -------------------------------------------------------------
  const applyCurrentEnhancements = useCallback((item) => {
    if (!item || !item.originalCanvas) return item;

    // 1. Perspective warp / cropped canvas
    let baseCanvas = item.originalCanvas;
    if (item.corners && item.corners.length === 4) {
      baseCanvas = perspectiveWarp(item.originalCanvas, item.corners);
    }

    // 2. Rotation
    if (item.rotation && item.rotation % 360 !== 0) {
      baseCanvas = rotateCanvas(baseCanvas, item.rotation);
    }

    // 3. CamScanner Filter & Adjustments
    const enhanced = enhanceImage(baseCanvas, {
      preset: item.preset || "original",
      brightness: item.brightness || 0,
      contrast: item.contrast || 0,
      sharpness: item.sharpness || 0,
      threshold: item.threshold,
    });

    const previewUrl = canvasToDataURL(enhanced, "image/jpeg", 0.92);

    return {
      ...item,
      enhancedCanvas: enhanced,
      previewUrl,
    };
  }, []);

  const updateActivePage = useCallback(
    (updater) => {
      if (mode === "document") {
        setPages((prev) => {
          if (!prev[activePageIndex]) return prev;
          const current = prev[activePageIndex];
          const updatedProps = typeof updater === "function" ? updater(current) : updater;
          const updatedItem = applyCurrentEnhancements({ ...current, ...updatedProps });
          const next = [...prev];
          next[activePageIndex] = updatedItem;
          return next;
        });
      } else {
        if (activeIdSlot === "front") {
          setIdFront((current) => {
            if (!current) return current;
            const updatedProps = typeof updater === "function" ? updater(current) : updater;
            return applyCurrentEnhancements({ ...current, ...updatedProps });
          });
        } else {
          setIdBack((current) => {
            if (!current) return current;
            const updatedProps = typeof updater === "function" ? updater(current) : updater;
            return applyCurrentEnhancements({ ...current, ...updatedProps });
          });
        }
      }
    },
    [mode, activePageIndex, activeIdSlot, applyCurrentEnhancements]
  );

  const activeItem = useMemo(() => {
    if (mode === "document") {
      return pages[activePageIndex] || null;
    }
    return activeIdSlot === "front" ? idFront : idBack;
  }, [mode, pages, activePageIndex, activeIdSlot, idFront, idBack]);

  // -------------------------------------------------------------
  // File Ingestion (PDF, JPEG, PNG, WEBP)
  // -------------------------------------------------------------
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setIsLoadingFile(true);

    try {
      const newPages = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          const renderedPages = await renderPdfToCanvases(file, { scale: 2.0 });

          for (const p of renderedPages) {
            const pageItem = applyCurrentEnhancements({
              id: `${Date.now()}-pdf-${p.pageNumber}`,
              originalCanvas: p.canvas,
              corners: null,
              rotation: 0,
              preset: "document",
              brightness: 0,
              contrast: 10,
              sharpness: 25,
              threshold: null,
            });

            newPages.push(pageItem);
          }
        } else if (file.type.startsWith("image/")) {
          const img = await loadImage(file);
          const canvas = createCanvas(img.naturalWidth, img.naturalHeight);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          const pageItem = applyCurrentEnhancements({
            id: `${Date.now()}-img-${i}`,
            originalCanvas: canvas,
            corners: null,
            rotation: 0,
            preset: "document",
            brightness: 0,
            contrast: 10,
            sharpness: 25,
            threshold: null,
          });

          newPages.push(pageItem);
        }
      }

      if (newPages.length === 0) {
        toast.error("No valid document or image could be loaded.");
        return;
      }

      if (mode === "document") {
        setPages((prev) => [...prev, ...newPages]);
        setCurrentStep(2);
        toast.success(`Loaded ${newPages.length} page(s) successfully!`);
      } else {
        if (activeIdSlot === "front" || !idFront) {
          setIdFront(newPages[0]);
          if (newPages[1]) {
            setIdBack(newPages[1]);
          }
          setActiveIdSlot("front");
        } else {
          setIdBack(newPages[0]);
          setActiveIdSlot("back");
        }
        setCurrentStep(2);
        toast.success("ID Card image loaded!");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to process file: " + (err.message || "Invalid file format"));
    } finally {
      setIsLoadingFile(false);
      setIsDraggingFile(false);
    }
  };

  // -------------------------------------------------------------
  // Camera Capture Flow
  // -------------------------------------------------------------
  const startCamera = async () => {
    try {
      setShowCameraModal(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: "environment" },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error("Camera access failed: " + (err.message || "No camera found"));
      setShowCameraModal(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  const captureCameraFrame = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = createCanvas(video.videoWidth || 1280, video.videoHeight || 720);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const pageItem = applyCurrentEnhancements({
      id: `${Date.now()}-cam`,
      originalCanvas: canvas,
      corners: null,
      rotation: 0,
      preset: "document",
      brightness: 0,
      contrast: 15,
      sharpness: 30,
      threshold: null,
    });

    if (mode === "document") {
      setPages((prev) => [...prev, pageItem]);
      setActivePageIndex(pages.length);
    } else {
      if (activeIdSlot === "front" || !idFront) {
        setIdFront(pageItem);
        setActiveIdSlot("front");
      } else {
        setIdBack(pageItem);
        setActiveIdSlot("back");
      }
    }

    stopCamera();
    setCurrentStep(2);
    toast.success("Captured scan from camera!");
  };

  // -------------------------------------------------------------
  // Filter & Adjustment Actions
  // -------------------------------------------------------------
  const handlePresetSelect = (presetId) => {
    updateActivePage({ preset: presetId });
  };

  const handleRotate = (angleDelta) => {
    if (!activeItem) return;
    const nextRotation = ((activeItem.rotation || 0) + angleDelta + 360) % 360;
    updateActivePage({ rotation: nextRotation });
  };

  const handleApplyAllPages = () => {
    if (!activeItem || mode !== "document") return;
    const { preset, brightness, contrast, sharpness, threshold } = activeItem;

    setPages((prev) =>
      prev.map((item) =>
        applyCurrentEnhancements({
          ...item,
          preset,
          brightness,
          contrast,
          sharpness,
          threshold,
        })
      )
    );
    toast.success("Applied filter settings to all pages!");
  };

  const handleResetAdjustments = () => {
    updateActivePage({
      preset: "original",
      brightness: 0,
      contrast: 0,
      sharpness: 0,
      threshold: null,
      rotation: 0,
      corners: null,
    });
    toast.info("Reset to original capture");
  };

  // -------------------------------------------------------------
  // 4-Corner Perspective Crop Mode
  // -------------------------------------------------------------
  const toggleCropMode = () => {
    if (!activeItem) return;
    setIsCropMode(true);
  };

  const handleCropConfirmed = ({ croppedCanvas, corners }) => {
    updateActivePage({
      processedSourceCanvas: croppedCanvas,
      corners,
    });
    setIsCropMode(false);
    toast.success("Crop & perspective deskew applied!");
  };

  const cancelCropMode = () => {
    setIsCropMode(false);
  };

  // -------------------------------------------------------------
  // Live Final A4 Print Preview
  // -------------------------------------------------------------
  const finalPreviewCanvas = useMemo(() => {
    if (mode === "document") {
      const activeCanvas = activeItem?.enhancedCanvas || null;
      return renderDocumentA4Layout(activeCanvas, {
        fitMode: a4FitMode,
        marginMm: a4MarginMm,
        orientation: a4Orientation,
        dpi: 150,
      });
    } else {
      const fCanvas = idFront?.enhancedCanvas || null;
      const bCanvas = idBack?.enhancedCanvas || null;
      return renderIdCardA4Layout(fCanvas, bCanvas, {
        layout: idLayout,
        showCuttingGuides: showCutGuides,
        dpi: 150,
      });
    }
  }, [mode, activeItem, idFront, idBack, a4FitMode, a4MarginMm, a4Orientation, idLayout, showCutGuides]);

  const finalPreviewDataUrl = useMemo(() => {
    if (!finalPreviewCanvas) return "";
    return canvasToDataURL(finalPreviewCanvas, "image/jpeg", 0.92);
  }, [finalPreviewCanvas]);

  // -------------------------------------------------------------
  // Print Job Execution
  // -------------------------------------------------------------
  const handlePrint = async () => {
    if (mode === "document" && pages.length === 0) {
      toast.error("Please upload at least one document to print.");
      return;
    }
    if (mode === "id_card" && !idFront && !idBack) {
      toast.error("Please upload at least one side of the ID card.");
      return;
    }

    setIsPrinting(true);
    setPrintProgressStep("Preparing high-resolution document...");

    try {
      let printCanvas;
      if (mode === "document") {
        printCanvas = renderDocumentA4Layout(activeItem?.enhancedCanvas, {
          fitMode: a4FitMode,
          marginMm: a4MarginMm,
          orientation: a4Orientation,
          dpi: 300,
        });
      } else {
        printCanvas = renderIdCardA4Layout(idFront?.enhancedCanvas, idBack?.enhancedCanvas, {
          layout: idLayout,
          showCuttingGuides: showCutGuides,
          dpi: 300,
        });
      }

      setPrintProgressStep("Processing CamScanner enhancements...");
      const base64Data = canvasToDataURL(printCanvas, "image/jpeg", 0.95);

      // Check for Electron native direct printing IPC
      if (window.jsmDesktop?.printers?.printDocument) {
        setPrintProgressStep(`Sending to Windows printer: ${selectedPrinter || "Default"}...`);
        const result = await window.jsmDesktop.printers.printDocument({
          dataUrl: base64Data,
          printerName: selectedPrinter,
          copies,
          color: printType === "color",
        });

        if (result?.success) {
          setPrintProgressStep("Print job sent successfully ✓");
          toast.success("Document sent to printer!");
        } else {
          throw new Error(result?.error || "Printer did not respond");
        }
      } else {
        // Fallback: Browser print preview
        setPrintProgressStep("Opening print preview...");
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>PrintQ — Document Print</title>
                <style>
                  @page { size: A4 ${a4Orientation}; margin: 0; }
                  body { margin: 0; padding: 0; background: #fff; display: flex; justify-content: center; align-items: center; }
                  img { width: 100vw; height: 100vh; object-fit: contain; }
                </style>
              </head>
              <body>
                <img src="${base64Data}" onload="window.print(); window.close();" />
              </body>
            </html>
          `);
          printWindow.document.close();
        }
        setPrintProgressStep("Printed ✓");
        toast.success("Print job dispatched!");
      }
    } catch (err) {
      console.error("Print execution error:", err);
      toast.error(
        err.message || "Selected printer is unavailable. Please check connection and try again."
      );
    } finally {
      setTimeout(() => {
        setIsPrinting(false);
        setPrintProgressStep("");
      }, 800);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 md:p-6 max-w-7xl mx-auto flex flex-col gap-6 select-none">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-5 backdrop-blur-md shadow-xl transition-all duration-300">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md shadow-amber-500/20 text-zinc-950 font-bold">
              <Printer className="w-4 h-4" />
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-black tracking-tight text-white flex items-center gap-1.5">
                <span>Print</span>
                <span className="text-amber-400">Q</span>
                <span className="text-zinc-400 font-medium text-lg ml-1">Studio</span>
              </h1>
              <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
                PRO
              </span>
            </div>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            CamScanner-style enhancement, A4 layout formatting & instant Xerox/CSC printing.
          </p>
        </div>

        {/* Mode Selector Toggle */}
        <div className="flex items-center bg-zinc-950 p-1.5 rounded-xl border border-zinc-800 shadow-inner">
          <button
            onClick={() => setMode("document")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${
              mode === "document"
                ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/25 scale-[1.02]"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4" />
            A4 Document
          </button>
          <button
            onClick={() => setMode("id_card")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${
              mode === "id_card"
                ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/25 scale-[1.02]"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <CreditCard className="w-4 h-4" />
            ID Card (Front + Back)
          </button>
        </div>
      </div>

      {/* Stepper Bar */}
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1">
        {[
          { num: 1, label: "Upload / Scan" },
          { num: 2, label: "Smart Enhance" },
          { num: 3, label: "Layout Sizing" },
          { num: 4, label: "Print Preview" },
          { num: 5, label: "Print" },
        ].map((s) => (
          <button
            key={s.num}
            onClick={() => setCurrentStep(s.num)}
            className={`flex-1 min-w-[120px] flex items-center justify-center gap-2.5 py-2.5 px-3 rounded-xl border transition-all duration-200 ${
              currentStep === s.num
                ? "bg-amber-500/10 border-amber-500/50 text-amber-400 font-bold shadow-sm"
                : currentStep > s.num
                ? "bg-zinc-900/80 border-emerald-500/30 text-emerald-400"
                : "bg-zinc-900/40 border-zinc-800 text-zinc-500"
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                currentStep === s.num
                  ? "bg-amber-500 text-zinc-950"
                  : currentStep > s.num
                  ? "bg-emerald-500 text-zinc-950"
                  : "bg-zinc-800 text-zinc-400"
              }`}
            >
              {currentStep > s.num ? "✓" : s.num}
            </span>
            <span className="text-xs">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1">
        {/* LEFT COLUMN: Controls & Enhance (7 Cols) */}
        <div className="lg:col-span-7 flex flex-col gap-5">
          {/* File Upload Zone */}
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-4 shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Upload className="w-4 h-4 text-amber-400" />
                Upload Document / Photo
              </h2>
              <span className="text-[11px] text-zinc-400">PDF, JPG, PNG, WEBP</span>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingFile(true);
              }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDraggingFile(false);
                handleFileUpload(e.dataTransfer.files);
              }}
              className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-200 text-center cursor-pointer relative group ${
                isDraggingFile
                  ? "border-amber-400 bg-amber-500/10 scale-[1.01]"
                  : "border-zinc-700/80 hover:border-amber-500/60 bg-zinc-950/40"
              }`}
            >
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={isLoadingFile}
              />
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform duration-200">
                <Upload className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-200">
                  Drag & drop files here, or <span className="text-amber-400 underline">browse</span>
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Single or multi-page PDFs, multiple images
                </p>
              </div>

              {isLoadingFile && (
                <div className="flex items-center gap-2 text-xs text-amber-400 animate-pulse mt-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Reading document pages...
                </div>
              )}
            </div>

            {/* Quick Camera Capture Button */}
            <button
              onClick={startCamera}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold border border-zinc-700 transition-all duration-150 active:scale-98"
            >
              <Camera className="w-4 h-4 text-emerald-400" />
              Scan with Camera / Webcam
            </button>
          </div>

          {/* ID Card Slots or Document Pages */}
          {mode === "id_card" ? (
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-4 shadow-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-400" />
                  Government ID Card Slots (85.6mm × 54mm)
                </h3>
                <span className="text-xs text-emerald-400 font-semibold">Standard Fixed Ratio</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Front Side */}
                <div
                  onClick={() => setActiveIdSlot("front")}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer flex flex-col gap-3 relative ${
                    activeIdSlot === "front"
                      ? "border-amber-500 bg-amber-500/5 shadow-lg shadow-amber-500/10 scale-[1.01]"
                      : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-amber-400 tracking-wider">FRONT SIDE</span>
                    {idFront && (
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-md font-bold">
                        ✓ Loaded
                      </span>
                    )}
                  </div>
                  <div className="aspect-[1.586] w-full rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden">
                    {idFront?.previewUrl ? (
                      <img src={idFront.previewUrl} alt="Front" className="w-full h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-zinc-500">
                        <Upload className="w-5 h-5" />
                        <span className="text-[11px]">Select Front Image</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Back Side */}
                <div
                  onClick={() => setActiveIdSlot("back")}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer flex flex-col gap-3 relative ${
                    activeIdSlot === "back"
                      ? "border-amber-500 bg-amber-500/5 shadow-lg shadow-amber-500/10 scale-[1.01]"
                      : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-amber-400 tracking-wider">BACK SIDE</span>
                    {idBack && (
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-md font-bold">
                        ✓ Loaded
                      </span>
                    )}
                  </div>
                  <div className="aspect-[1.586] w-full rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden">
                    {idBack?.previewUrl ? (
                      <img src={idBack.previewUrl} alt="Back" className="w-full h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-zinc-500">
                        <Upload className="w-5 h-5" />
                        <span className="text-[11px]">Select Back Image</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ID Card Output Layout Options */}
              <div className="flex items-center justify-between pt-2 border-t border-zinc-800/80">
                <div className="flex items-center gap-2 text-xs text-zinc-300">
                  <span>Layout on A4:</span>
                  <button
                    onClick={() => setIdLayout("stacked")}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                      idLayout === "stacked"
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                        : "bg-zinc-800 border-zinc-700 text-zinc-400"
                    }`}
                  >
                    Top & Bottom (Standard)
                  </button>
                  <button
                    onClick={() => setIdLayout("side_by_side")}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                      idLayout === "side_by_side"
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                        : "bg-zinc-800 border-zinc-700 text-zinc-400"
                    }`}
                  >
                    Side by Side
                  </button>
                </div>

                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCutGuides}
                    onChange={(e) => setShowCutGuides(e.target.checked)}
                    className="rounded border-zinc-700 text-amber-500 focus:ring-amber-500"
                  />
                  Cutting guide marks
                </label>
              </div>
            </div>
          ) : (
            /* DOCUMENT MULTI-PAGE STRIP */
            pages.length > 0 && (
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-3 shadow-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Layers className="w-4 h-4 text-amber-400" />
                    Document Pages ({pages.length})
                  </h3>
                  <button
                    onClick={handleApplyAllPages}
                    className="text-xs text-amber-400 hover:text-amber-300 font-semibold underline flex items-center gap-1"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Apply filter to all pages
                  </button>
                </div>

                <div className="flex items-center gap-3 overflow-x-auto py-2">
                  {pages.map((p, idx) => (
                    <div
                      key={p.id}
                      onClick={() => setActivePageIndex(idx)}
                      className={`relative flex-shrink-0 w-24 aspect-[0.707] rounded-xl border-2 transition-all duration-150 cursor-pointer overflow-hidden p-1 bg-zinc-950 flex flex-col ${
                        activePageIndex === idx
                          ? "border-amber-500 shadow-lg shadow-amber-500/20 scale-105"
                          : "border-zinc-800 hover:border-zinc-700 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <div className="flex-1 w-full overflow-hidden rounded">
                        <img src={p.previewUrl} alt={`Page ${idx + 1}`} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-400 px-1 font-semibold">
                        <span>P. {idx + 1}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPages((prev) => prev.filter((_, i) => i !== idx));
                            if (activePageIndex >= idx && activePageIndex > 0) {
                              setActivePageIndex(activePageIndex - 1);
                            }
                          }}
                          className="text-zinc-500 hover:text-red-400"
                          title="Delete page"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          {/* CAMSCANNER ENHANCEMENT CONTROLS */}
          {activeItem && (
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-4 shadow-lg animate-fade-in">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-bold text-white">Smart Enhance Tools</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetAdjustments}
                    className="text-xs px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setShowAdjustDrawer(!showAdjustDrawer)}
                    className={`text-xs px-3 py-1 rounded-lg border font-semibold flex items-center gap-1.5 transition-all ${
                      showAdjustDrawer
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                        : "bg-zinc-800 border-zinc-700 text-zinc-300"
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    Fine Adjust
                  </button>
                </div>
              </div>

              {/* 1-Tap Filter Presets Bar */}
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {PRESETS.map((pst) => {
                  const Icon = pst.icon;
                  const isSelected = (activeItem.preset || "original") === pst.id;
                  return (
                    <button
                      key={pst.id}
                      onClick={() => handlePresetSelect(pst.id)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border text-center transition-all duration-150 ${
                        isSelected
                          ? "bg-amber-500 text-zinc-950 font-black border-amber-400 shadow-md shadow-amber-500/20 scale-[1.02]"
                          : "bg-zinc-950/60 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-white"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-[10px] leading-tight truncate w-full">{pst.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Fine Sliders Drawer */}
              {showAdjustDrawer && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl bg-zinc-950/70 border border-zinc-800/80 animate-fade-in">
                  <div>
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span>Brightness</span>
                      <span className="text-amber-400 font-mono">{activeItem.brightness || 0}</span>
                    </div>
                    <input
                      type="range"
                      min="-60"
                      max="60"
                      value={activeItem.brightness || 0}
                      onChange={(e) => updateActivePage({ brightness: parseInt(e.target.value, 10) })}
                      className="w-full accent-amber-500 h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span>Contrast</span>
                      <span className="text-amber-400 font-mono">{activeItem.contrast || 0}</span>
                    </div>
                    <input
                      type="range"
                      min="-60"
                      max="60"
                      value={activeItem.contrast || 0}
                      onChange={(e) => updateActivePage({ contrast: parseInt(e.target.value, 10) })}
                      className="w-full accent-amber-500 h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span>Sharpness</span>
                      <span className="text-amber-400 font-mono">{activeItem.sharpness || 0}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={activeItem.sharpness || 0}
                      onChange={(e) => updateActivePage({ sharpness: parseInt(e.target.value, 10) })}
                      className="w-full accent-amber-500 h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span>B&W Cutoff</span>
                      <span className="text-amber-400 font-mono">{activeItem.threshold ?? "Auto"}</span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="210"
                      value={activeItem.threshold || 128}
                      onChange={(e) => updateActivePage({ threshold: parseInt(e.target.value, 10) })}
                      className="w-full accent-amber-500 h-1.5 bg-zinc-800 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Rotation & Crop Actions Bar */}
              <div className="flex items-center justify-between pt-2 border-t border-zinc-800/80">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRotate(-90)}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                    title="Rotate 90° Left"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleRotate(90)}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                    title="Rotate 90° Right"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={toggleCropMode}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${
                      isCropMode
                        ? "bg-emerald-500 text-zinc-950 border-emerald-400"
                        : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:text-white"
                    }`}
                  >
                    <Crop className="w-4 h-4" />
                    {isCropMode ? "Apply Crop" : "Perspective Crop"}
                  </button>
                  {isCropMode && (
                    <button
                      onClick={cancelCropMode}
                      className="text-xs text-zinc-400 hover:text-zinc-200 underline"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {/* Before / After Comparison Toggle */}
                <button
                  onMouseDown={() => setIsComparing(true)}
                  onMouseUp={() => setIsComparing(false)}
                  onTouchStart={() => setIsComparing(true)}
                  onTouchEnd={() => setIsComparing(false)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-400 text-xs font-bold border border-zinc-700 select-none active:scale-95 transition-all"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Hold for Original
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Print Preview & Trigger (5 Cols) */}
        <div className="lg:col-span-5 flex flex-col gap-5">
          <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-4 flex-1 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Printer className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-bold text-white">Final A4 Print Preview</h3>
              </div>
              <span className="text-[11px] text-zinc-400">
                {mode === "document" ? "A4 Sheet (210×297mm)" : "ID Card Layout (A4)"}
              </span>
            </div>

            {/* A4 Sheet Virtual Paper */}
            <div className="flex-1 min-h-[360px] bg-zinc-950 rounded-xl border border-zinc-800/80 p-4 flex items-center justify-center overflow-hidden">
              {finalPreviewDataUrl ? (
                <div className="relative shadow-2xl rounded-sm overflow-hidden bg-white max-h-[460px] aspect-[0.707] border border-zinc-400/40 transition-all duration-300">
                  <img
                    src={
                      isComparing && activeItem?.originalCanvas
                        ? canvasToDataURL(activeItem.originalCanvas)
                        : finalPreviewDataUrl
                    }
                    alt="A4 Preview"
                    className="w-full h-full object-contain"
                  />
                  {isComparing && (
                    <div className="absolute top-2 left-2 bg-black/80 text-amber-400 text-[10px] px-2 py-0.5 rounded font-bold">
                      Original View
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-zinc-600">
                  <FileText className="w-10 h-10 stroke-[1.2]" />
                  <span className="text-xs">No document loaded yet</span>
                </div>
              )}
            </div>

            {/* A4 Document Format Controls */}
            {mode === "document" && (
              <div className="flex items-center justify-between text-xs text-zinc-300 bg-zinc-950/60 p-3 rounded-xl border border-zinc-800/80">
                <div className="flex items-center gap-2">
                  <span>Fit:</span>
                  <button
                    onClick={() => setA4FitMode("fit")}
                    className={`px-2 py-0.5 rounded text-xs font-bold ${
                      a4FitMode === "fit" ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    Fit
                  </button>
                  <button
                    onClick={() => setA4FitMode("fill")}
                    className={`px-2 py-0.5 rounded text-xs font-bold ${
                      a4FitMode === "fill" ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    Fill
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span>Orientation:</span>
                  <button
                    onClick={() => setA4Orientation("portrait")}
                    className={`px-2 py-0.5 rounded text-xs font-bold ${
                      a4Orientation === "portrait" ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    Portrait
                  </button>
                  <button
                    onClick={() => setA4Orientation("landscape")}
                    className={`px-2 py-0.5 rounded text-xs font-bold ${
                      a4Orientation === "landscape" ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    Landscape
                  </button>
                </div>
              </div>
            )}

            {/* Print Options & Execution Bar */}
            <div className="flex flex-col gap-3 pt-3 border-t border-zinc-800">
              <div className="grid grid-cols-2 gap-3">
                {/* Print Mode Color vs B&W */}
                <div>
                  <label className="text-[11px] text-zinc-400 mb-1 block font-semibold">Print Mode</label>
                  <div className="flex bg-zinc-950 p-1 rounded-xl border border-zinc-800">
                    <button
                      onClick={() => setPrintType("bw")}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        printType === "bw"
                          ? "bg-zinc-200 text-zinc-950 shadow-sm"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      B&W
                    </button>
                    <button
                      onClick={() => setPrintType("color")}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        printType === "color"
                          ? "bg-amber-500 text-zinc-950 shadow-sm"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      Color
                    </button>
                  </div>
                </div>

                {/* Copies Counter */}
                <div>
                  <label className="text-[11px] text-zinc-400 mb-1 block font-semibold">Copies</label>
                  <div className="flex items-center bg-zinc-950 rounded-xl border border-zinc-800 p-1">
                    <button
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                      className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm flex items-center justify-center active:scale-95"
                    >
                      -
                    </button>
                    <span className="flex-1 text-center font-bold text-white text-sm">{copies}</span>
                    <button
                      onClick={() => setCopies(Math.min(99, copies + 1))}
                      className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm flex items-center justify-center active:scale-95"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Target Printer Selector */}
              <div>
                <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1 font-semibold">
                  <span>Target Windows Printer</span>
                  <span className="text-emerald-400">● Detected</span>
                </div>
                <select
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  {printers.length > 0 ? (
                    printers.map((p) => (
                      <option key={p.name} value={p.deviceName || p.name}>
                        {p.displayName || p.name} {p.isDefault ? "(Default)" : ""}
                      </option>
                    ))
                  ) : (
                    <option value="">{selectedPrinter || "Auto Default Printer"}</option>
                  )}
                </select>
              </div>

              {/* PRINT BUTTON */}
              <button
                onClick={handlePrint}
                disabled={isPrinting || (!pages.length && !idFront && !idBack)}
                className={`w-full py-4 px-6 rounded-xl font-bold text-base flex items-center justify-center gap-3 transition-all duration-200 shadow-xl ${
                  isPrinting
                    ? "bg-amber-600 text-zinc-950 cursor-wait animate-pulse"
                    : !pages.length && !idFront && !idBack
                    ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-400 via-amber-500 to-emerald-500 text-zinc-950 hover:brightness-110 shadow-amber-500/25 active:scale-[0.98]"
                }`}
              >
                {isPrinting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>{printProgressStep || "Printing..."}</span>
                  </>
                ) : (
                  <>
                    <Printer className="w-5 h-5" />
                    <span>Print Document ({copies} {copies === 1 ? "Copy" : "Copies"})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CAMERA SCAN MODAL */}
      {showCameraModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-2xl w-full p-6 flex flex-col gap-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-emerald-400" />
                Scan Document with Camera
              </h3>
              <button
                onClick={stopCamera}
                className="text-zinc-400 hover:text-white text-xs px-2.5 py-1 rounded-lg bg-zinc-800"
              >
                Close
              </button>
            </div>

            <div className="relative aspect-video rounded-xl bg-black overflow-hidden border border-zinc-800 flex items-center justify-center">
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
              <div className="absolute inset-8 border-2 border-emerald-400/60 rounded-xl pointer-events-none flex items-center justify-center">
                <span className="bg-black/60 text-emerald-400 text-xs px-3 py-1 rounded-full font-medium">
                  Align Document inside frame
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400">Hold document still in good lighting</span>
              <button
                onClick={captureCameraFrame}
                className="px-6 py-3 rounded-xl bg-emerald-500 text-zinc-950 font-bold text-sm hover:bg-emerald-400 transition-colors flex items-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95"
              >
                <Camera className="w-4 h-4" />
                Capture & Clean Scan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERSPECTIVE CROP & DESKEW MODAL */}
      {isCropMode && activeItem?.originalCanvas && (
        <DocumentCropEditor
          sourceCanvas={activeItem.originalCanvas}
          targetMode={mode}
          onConfirm={handleCropConfirmed}
          onCancel={cancelCropMode}
        />
      )}
    </div>
  );
}
